import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, ChatBubbleLeftIcon, EyeIcon, EyeSlashIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores/uiStore';
import { useOrderDetail, useOrderNotes, useUpdateOrderStatus, useAddOrderNote, useCreateRefund } from '@/hooks/useOrders';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { OrderStatusBadge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { ProductImage } from '@/components/ui/ProductImage';
import { formatCurrency, formatDateTime, formatRelativeTime, decodeHtmlEntities } from '@/utils/format';
import { type OrderStatus } from '@wooconnect/shared';
import { useOrderStatuses } from '@/hooks/useOrderStatuses';

const SYSTEM_NOTE_PATTERNS = [
  /^\[WPT\s/,
  /^Guest IP:/i,
  /^IP Address:/i,
  /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/,
  /\b[0-9a-f]{1,4}(:[0-9a-f]{0,4}){2,7}\b/i,
];

function isSystemNote(content: string): boolean {
  return SYSTEM_NOTE_PATTERNS.some((p) => p.test(content));
}

export function OrderDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderId: orderIdStr } = useParams();
  const orderId = Number(orderIdStr);
  const activeSiteId = useUIStore((s) => s.activeSiteId);

  const { data: order, isLoading, isError, refetch } = useOrderDetail(activeSiteId, isNaN(orderId) ? -1 : orderId);
  const { data: notes } = useOrderNotes(activeSiteId, orderId);
  const statusMutation = useUpdateOrderStatus(activeSiteId ?? '');
  const noteMutation = useAddOrderNote(activeSiteId ?? '');
  const refundMutation = useCreateRefund(activeSiteId ?? '');
  const orderStatuses = useOrderStatuses(activeSiteId);

  const [statusConfirm, setStatusConfirm] = useState<OrderStatus | null>(null);
  const [noteText, setNoteText] = useState('');
  const [isCustomerNote, setIsCustomerNote] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [showSystemNotes, setShowSystemNotes] = useState(false);

  if (!activeSiteId) {
    return <EmptyState icon={ShoppingBagIcon} title={t('dashboard.selectStore')} />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Skeleton className="h-64 w-full rounded-xl" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (isNaN(orderId) || isError || !order) {
    return <ErrorState onRetry={() => refetch()} />;
  }

  const currency = order.currency || undefined;

  const handleStatusChange = async () => {
    if (!statusConfirm) return;
    await statusMutation.mutateAsync({ orderId, status: statusConfirm });
    setStatusConfirm(null);
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    await noteMutation.mutateAsync({ orderId, note: noteText, customerNote: isCustomerNote });
    setNoteText('');
    setIsCustomerNote(false);
  };

  const handleRefund = async () => {
    const amount = parseFloat(refundAmount);
    if (!refundAmount || isNaN(amount) || amount <= 0 || amount > parseFloat(order.total)) return;
    await refundMutation.mutateAsync({ orderId, amount: refundAmount, reason: refundReason || undefined });
    setRefundOpen(false);
    setRefundAmount('');
    setRefundReason('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders')} leftIcon={<ArrowLeftIcon className="h-4 w-4" />}>
            {t('common.back')}
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('orders.order')} #{order.number}
          </h1>
          <OrderStatusBadge status={order.status} statusLabel={order.statusLabel} size="md" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setRefundOpen(true)}>
            {t('orders.refund')}
          </Button>
        </div>
      </div>

      {/* 2 Column Layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="space-y-6 lg:col-span-2">
          {/* Line Items */}
          <Card title={t('orders.items')}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="pb-2"></th>
                    <th className="pb-2">{t('products.name')}</th>
                    <th className="pb-2 text-center">{t('orders.items')}</th>
                    <th className="pb-2 text-right">{t('products.price')}</th>
                    <th className="pb-2 text-right">{t('orders.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.lineItems.map((item) => (
                    <tr key={item.id} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="py-3 pr-3">
                        <ProductImage src={item.image} alt={item.name} />
                      </td>
                      <td className="py-3">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{item.name}</div>
                        {item.sku && <div className="text-xs text-gray-500">{t('products.sku')}: {item.sku}</div>}
                      </td>
                      <td className="py-3 text-center text-gray-600 dark:text-gray-300">{item.quantity}</td>
                      <td className="py-3 text-right text-gray-600 dark:text-gray-300">{formatCurrency(item.price, currency)}</td>
                      <td className="py-3 text-right font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.total, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Notes */}
          <Card title={t('orders.notes')}>
            <div className="space-y-3">
              {notes && notes.length > 0 ? (
                <>
                  {notes.filter((note) => showSystemNotes || !isSystemNote(note.content)).map((note) => (
                    <div key={note.id} className={`rounded-lg border p-3 ${isSystemNote(note.content) ? 'border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/50' : 'border-gray-100 dark:border-gray-700'}`}>
                      <div className="mb-1 flex items-center gap-2">
                        <span className={`text-xs font-medium ${note.isCustomerNote ? 'text-brand-600 dark:text-brand-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          {note.isCustomerNote ? t('orders.customerNote') : note.author}
                        </span>
                        <span className="text-xs text-gray-400" title={formatDateTime(note.dateCreated)}>
                          {formatRelativeTime(note.dateCreated)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{decodeHtmlEntities(note.content)}</p>
                    </div>
                  ))}
                  {notes.some((n) => isSystemNote(n.content)) && (
                    <button
                      type="button"
                      onClick={() => setShowSystemNotes((v) => !v)}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showSystemNotes ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                      {showSystemNotes ? t('orders.hideSystemNotes') : t('orders.showSystemNotes')}
                    </button>
                  )}
                </>
              ) : (
                <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">{t('orders.noNotes')}</p>
              )}
            </div>

            {/* Add note */}
            <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-700">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder={t('orders.addNote')}
                rows={3}
                className="mb-2 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={isCustomerNote}
                    onChange={(e) => setIsCustomerNote(e.target.checked)}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                    {t('orders.sendToCustomer')}
                </label>
                <Button
                  size="sm"
                  onClick={handleAddNote}
                  loading={noteMutation.isPending}
                  disabled={!noteText.trim()}
                  leftIcon={<ChatBubbleLeftIcon className="h-4 w-4" />}
                >
                  {t('orders.addNote')}
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Status */}
          <Card title={t('orders.status')}>
            <div className="mb-3">
              <OrderStatusBadge status={order.status} statusLabel={order.statusLabel} size="md" />
            </div>
            <Select
              options={orderStatuses}
              value={order.status}
              onChange={(v) => { if (v !== order.status) setStatusConfirm(v as OrderStatus); }}
              placeholder={t('orders.changeStatus')}
            />
          </Card>

          {/* Customer */}
          <Card title={t('orders.customer')}>
            <div className="flex items-center gap-3">
              <Avatar name={order.customerName} size="md" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{order.customerName}</p>
                <a href={`mailto:${order.customerEmail}`} className="text-sm text-brand-600 hover:underline dark:text-brand-400">
                  {order.customerEmail}
                </a>
                {order.customerPhone && (
                  <a href={`tel:${order.customerPhone}`} className="block text-sm text-gray-500 dark:text-gray-400">
                    {order.customerPhone}
                  </a>
                )}
              </div>
            </div>
          </Card>

          {/* Order Totals */}
          <Card title={t('orders.total')}>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">{t('orders.subtotal')}</span>
                <span className="text-gray-900 dark:text-gray-100">{formatCurrency(order.subtotal, currency)}</span>
              </div>
              {order.shippingLines.map((sl, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{sl.methodTitle}</span>
                  <span className="text-gray-900 dark:text-gray-100">{formatCurrency(sl.total, currency)}</span>
                </div>
              ))}
              {Number(order.totalTax) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{t('orders.tax')}</span>
                  <span className="text-gray-900 dark:text-gray-100">{formatCurrency(order.totalTax, currency)}</span>
                </div>
              )}
              {order.couponLines.map((cl, i) => (
                <div key={i} className="flex justify-between text-green-600 dark:text-green-400">
                  <span>{t('orders.coupon')}: {cl.code}</span>
                  <span>-{formatCurrency(cl.discount, currency)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-gray-200 pt-2 dark:border-gray-700">
                <span className="text-base font-bold text-gray-900 dark:text-gray-100">{t('orders.total')}</span>
                <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                  {formatCurrency(order.total, currency)}
                </span>
              </div>
            </div>
          </Card>

          {/* Payment */}
          <Card title={t('orders.payment')}>
            <div className="space-y-1 text-sm">
              <p className="text-gray-900 dark:text-gray-100">{order.paymentMethodTitle}</p>
              {order.transactionId && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('orders.transactionId')}: {order.transactionId}</p>
              )}
              {order.datePaid && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('orders.datePaid')}: {formatDateTime(order.datePaid)}</p>
              )}
            </div>
          </Card>

          {/* Billing */}
          <Card title={t('orders.billing')}>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <p>{order.billing.firstName} {order.billing.lastName}</p>
              {order.billing.company && <p>{order.billing.company}</p>}
              <p>{order.billing.address1}</p>
              {order.billing.address2 && <p>{order.billing.address2}</p>}
              <p>{order.billing.city} {order.billing.postcode}</p>
              <p>{order.billing.country}</p>
            </div>
          </Card>

          {/* Shipping */}
          <Card title={t('orders.shippingAddress')}>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <p>{order.shipping.firstName} {order.shipping.lastName}</p>
              <p>{order.shipping.address1}</p>
              {order.shipping.address2 && <p>{order.shipping.address2}</p>}
              <p>{order.shipping.city} {order.shipping.postcode}</p>
              <p>{order.shipping.country}</p>
            </div>
          </Card>
        </div>
      </div>

      {/* Status change confirmation */}
      <ConfirmDialog
        isOpen={!!statusConfirm}
        onClose={() => setStatusConfirm(null)}
        onConfirm={handleStatusChange}
        title={t('orders.changeStatus')}
        message={t('orders.confirmStatusChange', { orderNumber: `#${order.number}`, status: t(`orders.statuses.${statusConfirm}`, statusConfirm ?? '') })}
        loading={statusMutation.isPending}
      />

      {/* Refund modal */}
      <Modal isOpen={refundOpen} onClose={() => setRefundOpen(false)} title={t('orders.refund')} size="sm">
        <div className="space-y-4">
          <Input
            label={t('orders.refundAmount')}
            type="number"
            step="0.01"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            placeholder={order.total}
          />
          <Input
            label={t('orders.refundReason')}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder={t('common.optional')}
          />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setRefundOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="danger" onClick={handleRefund} loading={refundMutation.isPending} disabled={!refundAmount}>
              {t('orders.refund')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
